import React, { useEffect, useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2, Car, Clock, AlertTriangle } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { useLanguage } from '../lib/i18n';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    deliveryJob,
    driverInfo,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const [searchingMinutes, setSearchingMinutes] = useState(0);
  const {
    t
  } = useLanguage();
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Safari / WhatsApp fallback using execCommand
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is blocked
      setCopied(false);
    }
  };
  // Calculate searching time
  useEffect(() => {
    if (order?.status === 'searching_driver' && deliveryJob?.created_at) {
      const interval = setInterval(() => {
        const diff = Math.floor((new Date().getTime() - new Date(deliveryJob.created_at).getTime()) / 60000);
        setSearchingMinutes(diff > 0 ? diff : 0);
      }, 10000);
      // Initial calculation
      const diff = Math.floor((new Date().getTime() - new Date(deliveryJob.created_at).getTime()) / 60000);
      setSearchingMinutes(diff > 0 ? diff : 0);
      return () => clearInterval(interval);
    }
  }, [order?.status, deliveryJob?.created_at]);
  if (loading) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-8475" data-mp="363131638y79s">
        <MarketplaceHeader data-id="element-8476" data-mp="3227i8ll3p8l4" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-8477" data-mp="322z8rhzucg4j">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-8478" data-mp="1uqgrnx5sf69o" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-8479" data-mp="1qo3n3c2s1eeh">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-8480" data-mp="1qmsixb5f64mu" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-8481" data-mp="66l0oxmm7s4n" />
            <Skeleton className="h-4 w-2/3" data-id="element-8482" data-mp="67w3ov88kvup" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-8483" data-mp="a6v2rz7adye8">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-8484" data-mp="f5oje0hve5im">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-8485" data-mp="jxg0lkf4yyw3" />
                <div className="flex-1" data-id="element-8486" data-mp="jyr6pqw20iqd">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-8487" data-mp="owsweunzjgat" />
                  <Skeleton className="h-4 w-full" data-id="element-8488" data-mp="ow9irq5dbe2l" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-8489" data-mp="xxh292386acm" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-8490" data-mp="xvedsmhq0pa0">
        <MarketplaceHeader data-id="element-8491" data-mp="12vsefjgzswy0" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-8492" data-mp="12txyykohpx1b">
          <EmptyState icon={Search} title={t('tracking_not_found')} description={t('error')} actionLabel={t('confirm_back')} onAction={() => window.location.href = '/'} data-id="element-8493" data-mp="2zqeyl7w7nl2b" />
        </main>
        <Footer data-id="element-8494" data-mp="24f748v1qowgl" />
      </div>;
  }
  const trackingUrl = window.location.href;
  const statusLabel = t(`status_${order.status}`) || getStatusLabel(order.status);
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-8495" data-mp="2ecuyzkzci3yr">
      <MarketplaceHeader data-id="element-8496" data-mp="2eaihf8xi1vm8" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8 sm:py-12" data-id="element-8497" data-mp="1qmr72avirsnl">
        <div className="flex items-center justify-between mb-8" data-id="element-8498" data-mp="1qko7p08h8n67">
          <h1 className="text-3xl font-bold text-gray-900" data-id="element-8499" data-mp="1lpha8a5swepq">
            {t('tracking_title')}
          </h1>
          <Badge variant="secondary" className={`text-sm px-3 py-1 ${getStatusColor(order.status)}`} data-id="element-8500" data-mp="20e51w5cj04ae">
            {statusLabel}
          </Badge>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-8501" data-mp="17u6l7de8mnta">
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-100" data-id="element-8502" data-mp="12vbyw1h2ah4f">
            <div data-id="element-8503" data-mp="3eip2gmfposv2">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-8504" data-mp="3jbi77ab6k175">
                {t('confirm_order_number')}
              </p>
              <p className="text-2xl font-black text-gray-900" data-id="element-8505" data-mp="34o6vn8f00h71">
                #{order.order_number}
              </p>
            </div>
            <div className="text-right" data-id="element-8506" data-mp="15vo2jyopkxz">
              <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-8507" data-mp="16x12tz9ghic">
                {t('total')}
              </p>
              <p className="text-2xl font-black text-brand-primary" data-id="element-8508" data-mp="62vrso82ablx">
                {formatCurrency(order.total)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8509" data-mp="3savebwbu0dop">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-8510" data-mp="2cuwoi16314d9">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-8511" data-mp="2gx9pyddsqg48">
                <Store className="w-5 h-5 text-blue-600" data-id="element-8512" data-mp="nd97kss6gmu7" />
              </div>
              <div data-id="element-8513" data-mp="ieo7ctcxe35r">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-8514" data-mp="ig91ihvbvk33">
                  Restaurante
                </h3>
                <p className="font-medium text-gray-900" data-id="element-8515" data-mp="dmochfbpq0g1">
                  Pedido del restaurante
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-8516" data-mp="8nk24ld1xwo4">
                  {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
                </p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-8517" data-mp="3lwg74txazrei">
              <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-8518" data-mp="1p2lbxxh6g2ri">
                <Receipt className="w-5 h-5 text-emerald-600" data-id="element-8519" data-mp="1k5uvva5dh763" />
              </div>
              <div data-id="element-8520" data-mp="1smdccnb1sm6n">
                <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-8521" data-mp="1sl2c2xgt9wka">
                  {t('total')}
                </h3>
                <p className="font-bold text-xl text-gray-900" data-id="element-8522" data-mp="1wl2o6bgftvo6">
                  {formatCurrency(order.total)}
                </p>
                <p className="text-sm text-gray-500 mt-1" data-id="element-8523" data-mp="26di7bc3h0qan">
                  Pago:{' '}
                  {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Driver Status Card (Only for delivery orders in specific states) */}
        {order.delivery_type === 'delivery' && <>
            {order.status === 'searching_driver' && <div className="bg-orange-50 rounded-2xl border border-orange-200 shadow-sm p-6 mb-8 flex items-center gap-4" data-id="element-8524" data-mp="2z1nvk8tqgxf8">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center shrink-0 animate-pulse" data-id="element-8525" data-mp="3k4x209gicjhm">
                  <Search className="w-6 h-6 text-orange-600" data-id="element-8526" data-mp="3g5o8n5pdqnml" />
                </div>
                <div data-id="element-8527" data-mp="3tw9sawmnr1lj">
                  <h3 className="font-bold text-orange-900 text-lg" data-id="element-8528" data-mp="3twt0hlmiabmd">
                    Buscando un conductor para tu pedido...
                  </h3>
                  <p className="text-orange-700 font-medium flex items-center gap-1.5 mt-1" data-id="element-8529" data-mp="7nryzxyln2wl">
                    <Clock className="w-4 h-4" data-id="element-8530" data-mp="2pq6ynj73t8z" />
                    Lleva {searchingMinutes}{' '}
                    {searchingMinutes === 1 ? 'minuto' : 'minutos'} buscando
                  </p>
                </div>
              </div>}

            {driverInfo && (order.status === 'ready' || order.status === 'out_for_delivery') && <div className="bg-cyan-50 rounded-2xl border border-cyan-200 shadow-sm p-6 mb-8 flex items-center gap-4" data-id="element-8531" data-mp="2dh9n0rte2p11">
                  <div className="w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center shrink-0" data-id="element-8532" data-mp="syxn8tjrjf03">
                    <Car className="w-6 h-6 text-cyan-600" data-id="element-8533" data-mp="xx8y97lsnvjw" />
                  </div>
                  <div className="flex-1" data-id="element-8534" data-mp="k7p1qjit3uj1">
                    <h3 className="font-bold text-cyan-900 text-lg" data-id="element-8535" data-mp="k6nelgoinhky">
                      {order.status === 'out_for_delivery' ? 'Tu pedido está en camino' : 'Tu conductor está en camino al restaurante'}
                    </h3>
                    <div className="mt-2 bg-white/60 rounded-xl p-3 border border-cyan-100" data-id="element-8536" data-mp="f8ltkq7j586u">
                      <p className="font-bold text-gray-900" data-id="element-8537" data-mp="19cq8r4fa8ka">
                        {driverInfo.name}
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-8538" data-mp="5bpjwh5wag0p">
                        {driverInfo.vehicleType} • {driverInfo.plate}
                      </p>
                    </div>
                  </div>
                </div>}
          </>}

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-8539" data-mp="3nlbixr43tk7c">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-8540" data-mp="3senvv3zf82k9">
            {t('tracking_title')}
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} restaurantId={order.restaurant_id} deliveryType={order.delivery_type} data-id="element-8541" data-mp="3dmdok7jzywgf" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-8542" data-mp="17l3i97ujq8g">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-8543" data-mp="58dg9cfxgd9h">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-8544" data-mp="5ahpm9wli4h5" />
              {t('checkout_address')}
            </h3>
            {order.delivery_zone && order.delivery_subzone && <div className="mb-3" data-id="element-8545" data-mp="2q072wihnhi7q">
                <p className="text-sm font-semibold text-gray-900" data-id="element-8546" data-mp="2if3axhlci3hr">
                  Zona:{' '}
                  <span className="font-normal text-gray-600" data-id="element-8547" data-mp="2dgiii4cc4o3v">
                    {order.delivery_zone}
                  </span>
                </p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-8548" data-mp="2sd0z6537g0ad">
                  Subzona:{' '}
                  <span className="font-normal text-gray-600" data-id="element-8549" data-mp="2nezhnolfg910">
                    {order.delivery_subzone}
                  </span>
                </p>
              </div>}
            <p className="text-gray-600" data-id="element-8550" data-mp="1ur3jefg7m924">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-8551" data-mp="29fgi3k6om60b">
                <span className="font-semibold" data-id="element-8552" data-mp="24mdjuyqym8ds">Notas:</span> {order.notes}
              </div>}
          </div>}

        {/* Tracking Link */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-8553" data-mp="1c02nk0adytms">
          <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-8554" data-mp="21nohn73o13hs">
            {t('copy_tracking')}
          </p>
          <div className="flex items-center gap-3" data-id="element-8555" data-mp="25o8gqg7suh6k">
            <div className="flex-1 bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-500 truncate border border-gray-100" data-id="element-8556" data-mp="25mxbsd2mb3dv">
              {trackingUrl}
            </div>
            <button onClick={handleCopyLink} className="shrink-0 flex items-center justify-center w-12 h-12 rounded-xl border-2 border-gray-200 hover:border-brand-primary hover:text-brand-primary transition-colors" data-id="element-8557" data-mp="2fdiae0iak7ws">
              {copied ? <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-8558" data-mp="1ro7gn2zczds5" /> : <Copy className="w-5 h-5" data-id="element-8559" data-mp="1wov81pri1wmw" />}
            </button>
          </div>
        </div>
      </main>

      <Footer data-id="element-8560" data-mp="333mc22bx5xl5" />
    </div>;
}